import { createElement, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type {
  CustomPostPreview,
  PersonalCustomEmoji,
  PreviewMessage,
  UploadedMedia
} from "../../api/client";
import { CustomEmoji } from "../custom-emoji/CustomEmoji";
import { useCustomEmojiLibrary } from "../custom-emoji/useCustomEmojiLibrary";

type TelegramPreviewProps = {
  preview: CustomPostPreview;
  mediaItems: UploadedMedia[];
  display?: "sheet" | "inline";
};

const messageCountLabel = (count: number) => {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return "сообщений";
  if (mod10 === 1) return "сообщение";
  if (mod10 >= 2 && mod10 <= 4) return "сообщения";
  return "сообщений";
};

const mediaLabel = (mediaType: NonNullable<PreviewMessage["media"]>[number]["mediaType"]) =>
  mediaType === "photo" ? "Фото" : mediaType === "video" ? "Видео" : "Документ";

const renderTelegramHtml = (
  value: string,
  emojiById: Map<string, PersonalCustomEmoji>,
  loadEmojiPreview: (customEmojiId: string, signal?: AbortSignal) => Promise<Blob>
): ReactNode[] => {
  const template = document.createElement("template");
  template.innerHTML = value;

  const renderNode = (node: Node, key: string): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (!(node instanceof HTMLElement)) return null;
    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes, (child, index) =>
      renderNode(child, `${key}:${index}`)
    );

    if (tag === "tg-emoji") {
      const customEmojiId = node.getAttribute("emoji-id")?.trim() ?? "";
      const fallback = node.textContent?.trim() || "▫️";
      const emoji = emojiById.get(customEmojiId);
      return emoji
        ? <CustomEmoji emoji={emoji} loadPreview={loadEmojiPreview} size="preview" key={key} />
        : <span className="custom-emoji-inline-fallback" role="img" aria-label={fallback} data-custom-emoji-id={customEmojiId} key={key}>{fallback}</span>;
    }
    if (tag === "br") return "\n";
    if (tag === "b" || tag === "strong") return createElement("strong", { key }, children);
    if (tag === "i" || tag === "em") return createElement("em", { key }, children);
    if (tag === "u" || tag === "ins") return createElement("u", { key }, children);
    if (tag === "s" || tag === "strike" || tag === "del") return createElement("s", { key }, children);
    if (tag === "code") return <code className="telegram-inline-code" key={key}>{children}</code>;
    if (tag === "pre") return <pre className="telegram-inline-pre" key={key}>{children}</pre>;
    if (tag === "a") return <span className="telegram-inline-link" key={key}>{children}</span>;
    if (tag === "blockquote") {
      return <blockquote className="telegram-blockquote" data-expandable={node.hasAttribute("expandable") || undefined} key={key}>{children}</blockquote>;
    }
    if (tag === "tg-spoiler" || tag === "span" && node.classList.contains("tg-spoiler")) {
      return <span className="telegram-spoiler" key={key}>{children}</span>;
    }
    return children;
  };

  return Array.from(template.content.childNodes, (node, index) => renderNode(node, String(index)));
};

function TelegramMedia({
  media,
  uploaded
}: {
  media: NonNullable<PreviewMessage["media"]>[number];
  uploaded: UploadedMedia | undefined;
}) {
  const label = mediaLabel(media.mediaType);
  const fileName = uploaded?.fileName;
  const localUrl = uploaded?.localPreviewUrl;

  if (media.mediaType === "video") {
    return localUrl ? (
      <video
        className="telegram-media telegram-media-video"
        src={localUrl}
        aria-label={fileName ? `Видео ${fileName}` : "Видео загружено"}
        muted
        playsInline
        preload="metadata"
        style={uploaded?.width && uploaded?.height
          ? { aspectRatio: `${uploaded.width} / ${uploaded.height}` }
          : undefined}
      />
    ) : (
      <div className="telegram-media telegram-media-poster" role="img" aria-label="Видео загружено">
        <span aria-hidden="true">▶</span>
        <strong>Видео загружено</strong>
      </div>
    );
  }

  if (media.mediaType === "photo") {
    return localUrl ? (
      <img
        className="telegram-media telegram-media-photo"
        src={localUrl}
        alt={fileName ? `Фото ${fileName}` : "Фото загружено"}
      />
    ) : (
      <div className="telegram-media telegram-media-poster" role="img" aria-label="Фото загружено">
        <span aria-hidden="true">▣</span>
        <strong>Фото загружено</strong>
      </div>
    );
  }

  return (
    <div className="telegram-document" role="img" aria-label={fileName ? `Документ ${fileName}` : "Документ загружен"}>
      <span aria-hidden="true">📄</span>
      <strong>{fileName ?? `${label} загружен`}</strong>
    </div>
  );
}

function TelegramMessage({
  message,
  mediaItems,
  index,
  emojiById,
  loadEmojiPreview
}: {
  message: PreviewMessage;
  mediaItems: UploadedMedia[];
  index: number;
  emojiById: Map<string, PersonalCustomEmoji>;
  loadEmojiPreview: (customEmojiId: string, signal?: AbortSignal) => Promise<Blob>;
}) {
  const text = message.text !== undefined ? (
    <div className="telegram-message-text" data-truncated={message.truncated || undefined}>
      {renderTelegramHtml(message.text, emojiById, loadEmojiPreview)}
    </div>
  ) : null;
  const media = message.media?.length ? (
    <div className="telegram-media-list">
      {message.media.map((item, mediaIndex) => (
        <TelegramMedia
          media={item}
          uploaded={mediaItems[item.index]}
          key={`${item.index}:${mediaIndex}`}
        />
      ))}
    </div>
  ) : null;
  const textFirst = message.layout === "text_first" || message.showCaptionAboveMedia === true;
  const content: ReactNode = textFirst ? <>{text}{media}</> : <>{media}{text}</>;

  return (
    <article className={`telegram-message telegram-message-${message.kind}`} aria-label={`Сообщение ${index + 1}`}>
      {content}
      {message.keyboard?.map((row, rowIndex) => (
        <div
          className={`telegram-keyboard-row telegram-keyboard-${message.buttonAlignment ?? "center"}`}
          role="group"
          aria-label={`Строка кнопок ${rowIndex + 1}`}
          style={{ gridTemplateColumns: `repeat(${Math.max(row.length, 1)}, minmax(0, 1fr))` }}
          key={rowIndex}
        >
          {row.map((button, buttonIndex) => (
            <span
              className={`telegram-preview-button telegram-preview-button-${button.style ?? "default"}`}
              role="button"
              aria-disabled="true"
              key={buttonIndex}
            >
              {button.customEmojiId && (emojiById.get(button.customEmojiId)
                ? <CustomEmoji emoji={emojiById.get(button.customEmojiId) as PersonalCustomEmoji} loadPreview={loadEmojiPreview} size="preview" />
                : <span className="custom-emoji-inline-fallback" aria-hidden="true">{button.emojiFallback ?? "▫️"}</span>)}
              {button.customEmojiId ? " " : ""}{button.text}
            </span>
          ))}
        </div>
      ))}
    </article>
  );
}

export function TelegramPreview({ preview, mediaItems, display = "sheet" }: TelegramPreviewProps) {
  const emojiLibrary = useCustomEmojiLibrary();
  const emojiById = new Map(
    emojiLibrary.sets.flatMap((set) => set.emoji).map((emoji) => [emoji.customEmojiId, emoji])
  );
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef(false);

  const close = () => {
    restoreFocusRef.current = true;
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const content = triggerRef.current?.closest<HTMLElement>(".composer-content") ?? null;
    const contentWasInert = content?.hasAttribute("inert") ?? false;
    content?.setAttribute("inert", "");
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
      ) ?? []);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (!contentWasInert) content?.removeAttribute("inert");
    };
  }, [open]);

  useEffect(() => {
    if (open || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    triggerRef.current?.focus();
  }, [open]);

  const count = preview.messages.length;
  const formatLabel = preview.effectiveFormat === "rich" ? "Новый Rich · бета" : "Обычный";
  if (display === "inline") {
    return (
      <div className="telegram-preview telegram-preview-inline">
        <span className="telegram-format-chip">{formatLabel}</span>
        {preview.warnings.map((warning, index) => (
          <div className={`telegram-warning telegram-warning-${warning.severity}`} role="status" key={index}>
            <span aria-hidden="true">{warning.severity === "warning" ? "!" : "i"}</span>
            <p>{warning.text}</p>
          </div>
        ))}
        <div className="telegram-message-list" aria-label="Сообщения Telegram">
          {preview.messages.map((message, index) => (
            <TelegramMessage message={message} mediaItems={mediaItems} index={index} emojiById={emojiById} loadEmojiPreview={emojiLibrary.loadPreview} key={index} />
          ))}
        </div>
      </div>
    );
  }
  const sheet = open && typeof document !== "undefined" ? createPortal(
    <div
      className="telegram-sheet-backdrop"
      onClick={(event) => { if (event.target === event.currentTarget) close(); }}
    >
      <section
        className="telegram-sheet"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="telegram-sheet-header">
          <div>
            <p className="eyebrow">{formatLabel}</p>
            <h2 id={titleId}>Предпросмотр Telegram</h2>
          </div>
          <button ref={closeButtonRef} type="button" aria-label="Закрыть предпросмотр" onClick={close}>×</button>
        </header>
        {preview.warnings.map((warning, index) => (
          <div className={`telegram-warning telegram-warning-${warning.severity}`} role="status" key={index}>
            <span aria-hidden="true">{warning.severity === "warning" ? "!" : "i"}</span>
            <p>{warning.text}</p>
          </div>
        ))}
        <div className="telegram-message-list" aria-label="Сообщения Telegram">
          {preview.messages.map((message, index) => (
            <TelegramMessage message={message} mediaItems={mediaItems} index={index} emojiById={emojiById} loadEmojiPreview={emojiLibrary.loadPreview} key={index} />
          ))}
        </div>
      </section>
    </div>,
    document.body
  ) : null;

  return (
    <div className="telegram-preview">
      <span className="telegram-format-chip">{formatLabel}</span>
      <button className="telegram-preview-trigger" ref={triggerRef} type="button" onClick={() => setOpen(true)}>
        Как в Telegram: {count} {messageCountLabel(count)}
      </button>
      {sheet}
    </div>
  );
}
